import { useEffect, useRef, useState } from "react";
import { PALETTE, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "./tokens";

/* OrgPulse — SVG hub-and-spoke visualization showing CoBrain at the
   center with employee agents on the spokes. Signals animate from
   spokes → hub → back out as actions on a 4s cycle. Pure CSS
   keyframes, no framer-motion required. Pauses for prefers-reduced-
   motion. ~5kb runtime. */

type Spoke = {
  label: string;
  role: string;
  signal: string;
  action: string;
  hue: string;
  angle: number; // degrees
};

const SPOKES: Spoke[] = [
  { label: "Sales", role: "Account exec", signal: "deal at risk", action: "follow-up drafted", hue: "#7CB7A8", angle: -90 },
  { label: "Ops",   role: "Dispatcher",   signal: "Tuesday slip",  action: "rebalanced route", hue: "#E8B458", angle: -18 },
  { label: "Finance", role: "Bookkeeper", signal: "billing leak",  action: "invoice queued",  hue: "#9B7AB8", angle: 54 },
  { label: "Support", role: "CS lead",    signal: "VIP waiting",   action: "owner pinged",    hue: "#C4823A", angle: 126 },
  { label: "Owner",   role: "You",        signal: "approve / pause", action: "decision logged", hue: "#1a1a1a", angle: 198 },
];

const RADIUS = 150;
const CENTER = 200;

export const OrgPulse = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} style={{
      background: PALETTE.dark, color: "#fff", borderRadius: 24,
      padding: "clamp(28px, 4vw, 44px)",
      position: "relative", overflow: "hidden",
      display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)",
      gap: 32, alignItems: "center",
    }} className="cb-orgpulse">
      <style>{`
        @media (max-width: 880px) {
          .cb-orgpulse { grid-template-columns: 1fr !important; gap: 20px !important; }
          .cb-orgpulse-svg { max-width: 360px; margin: 0 auto; }
        }
        @keyframes cb-pulse-in  { 0% { opacity: 0; r: 2 } 35% { opacity: 1; r: 6 } 100% { opacity: 0; r: 2 } }
        @keyframes cb-pulse-out { 0% { opacity: 0; r: 2 } 35% { opacity: 1; r: 6 } 100% { opacity: 0; r: 2 } }
        @keyframes cb-hub-glow  { 50% { opacity: 0.85 } }
        @keyframes cb-spoke-pulse { 50% { opacity: 0.55 } }
        @media (prefers-reduced-motion: reduce) {
          .cb-pulse-anim { animation: none !important; opacity: 0.5 !important; }
        }
      `}</style>

      <div aria-hidden style={{
        position: "absolute", inset: "-30% -10% auto auto", width: 520, height: 520,
        background: `radial-gradient(circle, ${PALETTE.amber}26 0%, transparent 60%)`,
        filter: "blur(60px)", pointerEvents: "none",
      }} />

      <div className="cb-orgpulse-svg" style={{ position: "relative", zIndex: 1 }}>
        <svg viewBox="0 0 400 400" width="100%" style={{ display: "block", maxHeight: 420 }} role="img" aria-label="CoBrain hub with company agents on spokes">
          {/* spokes */}
          {SPOKES.map((s) => {
            const rad = (s.angle * Math.PI) / 180;
            const x = CENTER + Math.cos(rad) * RADIUS;
            const y = CENTER + Math.sin(rad) * RADIUS;
            return (
              <g key={s.label}>
                <line x1={CENTER} y1={CENTER} x2={x} y2={y}
                  stroke={s.hue} strokeWidth={1} strokeOpacity={0.28}
                  className="cb-pulse-anim"
                  style={{ animation: inView ? `cb-spoke-pulse 3.6s ease-in-out ${s.angle / 90}s infinite` : undefined }} />
                {/* signal pulse traveling along the spoke (SMIL animateMotion) */}
                <circle r={3.2} fill={s.hue} opacity={inView ? 0.95 : 0}>
                  {inView && (
                    <animateMotion
                      dur="3.6s"
                      repeatCount="indefinite"
                      begin={`${(Math.abs(s.angle) % 360) / 180}s`}
                      path={`M ${x} ${y} L ${CENTER} ${CENTER}`}
                    />
                  )}
                  {inView && (
                    <animate attributeName="opacity" values="0;1;0" dur="3.6s" repeatCount="indefinite"
                      begin={`${(Math.abs(s.angle) % 360) / 180}s`} />
                  )}
                </circle>
                {/* node */}
                <circle cx={x} cy={y} r={22}
                  fill="#1f1c19" stroke={s.hue} strokeWidth={1.5} strokeOpacity={0.55} />
                <text x={x} y={y + 4} textAnchor="middle"
                  fontFamily="'JetBrains Mono', monospace" fontSize={11}
                  fill={s.hue} fontWeight={700} letterSpacing="0.1em"
                  style={{ textTransform: "uppercase" }}>
                  {s.label.slice(0, 3).toUpperCase()}
                </text>
                <text x={x} y={y + 40} textAnchor="middle"
                  fontFamily="'JetBrains Mono', monospace" fontSize={9.5}
                  fill="rgba(255,255,255,0.45)" letterSpacing="0.08em"
                  style={{ textTransform: "uppercase" }}>
                  {s.role}
                </text>
              </g>
            );
          })}

          {/* hub */}
          <circle cx={CENTER} cy={CENTER} r={56}
            fill="rgba(196,130,58,0.10)" stroke={PALETTE.amber} strokeWidth={1.2} strokeOpacity={0.6} />
          <circle cx={CENTER} cy={CENTER} r={36}
            fill={PALETTE.amber} fillOpacity={0.18} stroke={PALETTE.amberSoft} strokeWidth={1}
            className="cb-pulse-anim"
            style={{ animation: inView ? "cb-hub-glow 2.4s ease-in-out infinite" : undefined }} />
          <text x={CENTER} y={CENTER - 4} textAnchor="middle"
            fontFamily="'Instrument Serif', serif" fontSize={22} fontStyle="italic"
            fill="#fff" fontWeight={400}>CoBrain</text>
          <text x={CENTER} y={CENTER + 14} textAnchor="middle"
            fontFamily="'JetBrains Mono', monospace" fontSize={9}
            fill={PALETTE.amberSoft} letterSpacing="0.18em"
            style={{ textTransform: "uppercase" }}>shared context</text>
        </svg>
      </div>

      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.amberSoft, fontWeight: 700 }}>
          Org pulse · live signal flow
        </div>
        <h3 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(26px, 3vw, 36px)", lineHeight: 1.1, fontWeight: 400, margin: 0, color: "#fff" }}>
          Every employee's signal lands in <span style={{ color: PALETTE.amberSoft }}>one brain.</span>
        </h3>
        <p style={{ fontSize: 14.5, color: "rgba(255,255,255,0.72)", lineHeight: 1.55, margin: 0, fontFamily: FONT_BODY }}>
          Sales sees a deal at risk. Ops catches a routing slip. Finance flags a billing leak. CoBrain stitches them into shared context — and pushes the next action back out, with your approval gates in place.
        </p>
        <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "flex", flexDirection: "column", gap: 8 }}>
          {SPOKES.slice(0, 4).map((s) => (
            <li key={s.label} style={{
              display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 10, alignItems: "center",
              padding: "8px 12px", borderRadius: 10,
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)",
              fontFamily: FONT_MONO, fontSize: 11.5, color: "rgba(255,255,255,0.78)",
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.hue }} />
              <span><span style={{ color: s.hue, fontWeight: 700, letterSpacing: "0.06em" }}>{s.label.toUpperCase()}</span> · {s.signal}</span>
              <span style={{ color: PALETTE.amberSoft, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10.5 }}>→ {s.action}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
